import { gsap } from "gsap";

/** Tiny deep merge (enough for options objects) */
function deepMerge(target, ...sources) {
    for (const src of sources) {
        if (!src || typeof src !== "object") continue;
        for (const [k, v] of Object.entries(src)) {
            if (v && typeof v === "object" && !Array.isArray(v)) {
                target[k] = deepMerge(target[k] && typeof target[k] === "object" ? target[k] : {}, v);
            } else {
                target[k] = v;
            }
        }
    }
    return target;
}

function isHTMLElement(el) {
    return el instanceof HTMLElement || el instanceof SVGElement;
}

function closestMatching(target, selector) {
    if (!target) return null;
    if (target.closest) return target.closest(selector);
    return null;
}

export class Cursor {
    constructor(options = {}) {
        this.options = deepMerge(
            {
                container: "body",
                speed: 0.7,
                ease: "expo.out",
                visibleTimeout: 300,
                stickLerp: 0.15,
                pointerSelectors: "a,input,textarea,button,select,label,[role='button']",
            },
            options
        );

        const containerEl =
            typeof this.options.container === "string"
                ? document.querySelector(this.options.container)
                : this.options.container;

        this.container = isHTMLElement(containerEl) ? containerEl : document.body;

        this.visible = false;
        this.visibleTimer = null;

        this.pos = { x: -window.innerWidth, y: -window.innerHeight };
        this.stick = null;

        this.el = document.createElement("div");
        this.el.className = "cb-cursor";

        this.textEl = document.createElement("div");
        this.textEl.className = "cb-cursor-text";

        this.el.appendChild(this.textEl);
        this.container.appendChild(this.el);

        this.bind();
        this.move(this.pos.x, this.pos.y, 0);
    }

    bind() {
        // Mouse presence
        this.container.addEventListener("mouseenter", () => this.show());
        this.container.addEventListener("mouseleave", () => this.hide());

        // Mouse move
        this.container.addEventListener("mousemove", (e) => {
            const targetX = e.clientX;
            const targetY = e.clientY;

            if (this.stick) {
                // lerp toward pointer from stick center
                this.pos.x = this.stick.x - (this.stick.x - targetX) * this.options.stickLerp;
                this.pos.y = this.stick.y - (this.stick.y - targetY) * this.options.stickLerp;
            } else {
                this.pos.x = targetX;
                this.pos.y = targetY;
            }

            this.update();
        });

        // Active state
        this.container.addEventListener("mousedown", () => this.setState("-active"));
        this.container.addEventListener("mouseup", () => this.removeState("-active"));

        // Delegated hover handling
        this.container.addEventListener("mouseover", (e) => {
            const t = e.target;

            // Pointer state for common interactives
            if (closestMatching(t, this.options.pointerSelectors)) {
                this.setState("-pointer");
            }

            // Hide over iframe
            if (closestMatching(t, "iframe")) {
                this.hide();
            }

            // data-cursor => add that class
            const cursorEl = closestMatching(t, "[data-cursor]");
            if (cursorEl) {
                this.setState(cursorEl.getAttribute("data-cursor"));
            }

            // data-cursor-text => set text + -text
            const cursorTextEl = closestMatching(t, "[data-cursor-text]");
            if (cursorTextEl) {
                this.setText(cursorTextEl.getAttribute("data-cursor-text") || "");
            }

            // data-cursor-stick => stick to selector or element
            const stickEl = closestMatching(t, "[data-cursor-stick]");
            if (stickEl) {
                this.setStick(stickEl.getAttribute("data-cursor-stick"));
            }
        });

        this.container.addEventListener("mouseout", (e) => {
            const t = e.target;

            if (closestMatching(t, this.options.pointerSelectors)) {
                this.removeState("-pointer");
            }

            if (closestMatching(t, "iframe")) {
                this.show();
            }

            const cursorEl = closestMatching(t, "[data-cursor]");
            if (cursorEl) {
                this.removeState(cursorEl.getAttribute("data-cursor"));
            }

            const cursorTextEl = closestMatching(t, "[data-cursor-text]");
            if (cursorTextEl) {
                this.removeText();
            }

            const stickEl = closestMatching(t, "[data-cursor-stick]");
            if (stickEl) {
                this.removeStick();
            }
        });
    }

    setState(state) {
        if (!state) return;
        this.el.classList.add(state);
    }

    removeState(state) {
        if (!state) return;
        this.el.classList.remove(state);
    }

    toggleState(state) {
        if (!state) return;
        this.el.classList.toggle(state);
    }

    setText(text) {
        this.textEl.innerHTML = text;
        this.el.classList.add("-text");
    }

    removeText() {
        this.el.classList.remove("-text");
        this.textEl.innerHTML = "";
    }

    setStick(stickValue) {
        // stickValue can be:
        // - a selector (".card", "#id")
        // - "this" meaning the element with [data-cursor-stick]
        let target = null;

        if (!stickValue || stickValue === "this") {
            // if value is empty/this, stick to the element that triggered the handler
            // we can't access it directly here, so fall back to: nearest hovered stick element
            // (good enough for typical usage)
            target = document.querySelector("[data-cursor-stick]:hover");
        } else {
            target = document.querySelector(stickValue);
        }

        if (!isHTMLElement(target)) return;

        const rect = target.getBoundingClientRect();
        this.stick = {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
            target,
        };

        this.move(this.stick.x, this.stick.y, 0.15);
    }

    removeStick() {
        this.stick = null;
    }

    update() {
        this.move();
        this.show();
    }

    move(x, y, duration) {
        const tx = typeof x === "number" ? x : this.pos.x;
        const ty = typeof y === "number" ? y : this.pos.y;

        gsap.to(this.el, {
            x: tx,
            y: ty,
            force3D: true,
            overwrite: true,
            ease: this.options.ease,
            duration: this.visible ? (typeof duration === "number" ? duration : this.options.speed) : 0,
        });
    }

    show() {
        if (this.visible) return;
        clearTimeout(this.visibleTimer);
        this.el.classList.add("-visible");
        this.visibleTimer = setTimeout(() => {
            this.visible = true;
        }, 0);
    }

    hide() {
        clearTimeout(this.visibleTimer);
        this.el.classList.remove("-visible");
        this.visibleTimer = setTimeout(() => {
            this.visible = false;
        }, this.options.visibleTimeout);
    }

    destroy() {
        clearTimeout(this.visibleTimer);
        this.container?.removeChild(this.el);
        // If you need full cleanup, store bound handlers and removeEventListener them.
    }
}

// Init cursor
const cursor = new Cursor();
export default cursor;
